import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Copy, Send, Sparkles, Download } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { formatDateTime } from "@/components/admin/ui";
import { COMMERCIAL_STATUS_META, formatCents, dollarsToCents, centsToDollars } from "./salesMeta";

const labelClass = "text-xs font-bold uppercase tracking-[0.12em] text-slate-400";
const inputClass = "mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-white/15 dark:bg-[#0B1220] dark:text-white";
const btn = "inline-flex items-center gap-1.5 rounded-xl border border-transparent bg-[#0052FF] px-3.5 py-2 text-xs font-bold text-white transition-colors duration-150 hover:bg-[#0040CC] disabled:cursor-not-allowed disabled:opacity-50";
const outlineBtn = "inline-flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2 text-xs font-bold text-slate-600 transition-colors duration-150 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/15 dark:text-slate-300 dark:hover:bg-white/10";

const blankRow = () => ({ source_item_type: "custom", source_item_id: null, source_business_item_id: null, name: "", description: null, sku: null, quantity: 1, unit_price_cents: 0, line_discount_cents: 0 });

export const QuoteProposalTab = ({ lead, businessItems, onRefresh, onError, API }) => {
  const [subView, setSubView] = useState("quote");
  const [quoteData, setQuoteData] = useState(null);
  const [rows, setRows] = useState(null);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState("0");
  const [taxRate, setTaxRate] = useState("0");
  const [feeDollars, setFeeDollars] = useState("0");
  const [paymentTerms, setPaymentTerms] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const [proposalData, setProposalData] = useState(null);
  const [templateId, setTemplateId] = useState("");
  const [leadFiles, setLeadFiles] = useState(null);
  const [selectedFileIds, setSelectedFileIds] = useState([]);

  const loadQuote = useCallback(() => {
    axios.get(`${API}/org/sales/leads/${lead.lead_id}/quote`, { withCredentials: true }).then(({ data }) => {
      setQuoteData(data);
      if (data.quote) {
        const v = data.quote.versions[data.quote.current_version - 1];
        setRows(v.items.map((i) => ({ ...i })));
        setDiscountType(v.discount_type); setDiscountValue(String(v.discount_type === "fixed" ? centsToDollars(v.discount_value) : v.discount_value / 100));
        setTaxRate(String(v.tax_rate_bp / 100)); setFeeDollars(centsToDollars(v.fee_cents));
        setPaymentTerms(v.payment_terms || ""); setValidUntil(v.valid_until || ""); setNotes(v.notes || "");
      } else if (businessItems?.length) {
        setRows(businessItems.map((i) => ({
          source_item_type: i.item_type, source_item_id: i.item_id, source_business_item_id: i.business_item_id,
          name: i.catalog?.name || i.item_name_snapshot, description: null, sku: i.catalog?.sku || null,
          quantity: i.quantity, unit_price_cents: i.catalog?.price_cents || 0, line_discount_cents: 0,
        })));
      } else {
        setRows([]);
      }
    }).catch((err) => onError(formatApiErrorDetail(err.response?.data?.detail)));
  }, [API, lead.lead_id, businessItems, onError]);

  const loadProposal = useCallback(() => {
    axios.get(`${API}/org/sales/leads/${lead.lead_id}/proposal`, { withCredentials: true }).then(({ data }) => setProposalData(data)).catch((err) => onError(formatApiErrorDetail(err.response?.data?.detail)));
    axios.get(`${API}/org/sales/leads/${lead.lead_id}/files`, { withCredentials: true }).then(({ data }) => setLeadFiles(data.files)).catch(() => {});
  }, [API, lead.lead_id, onError]);

  useEffect(() => { loadQuote(); }, [loadQuote]);
  useEffect(() => { if (subView === "proposal") loadProposal(); }, [subView, loadProposal]);

  if (!quoteData || rows === null) return <p className="text-sm text-slate-400" data-testid="quote-proposal-loading">Loading…</p>;

  const { quote, quote_eligible } = quoteData;

  const updateRow = (idx, field, value) => setRows((r) => r.map((row, i) => (i === idx ? { ...row, [field]: value } : row)));
  const removeRow = (idx) => setRows((r) => r.filter((_, i) => i !== idx));
  const addRow = () => setRows((r) => [...r, blankRow()]);

  const buildPayload = () => ({
    items: rows.map((r) => ({ ...r, quantity: Number(r.quantity) || 1, unit_price_cents: Number(r.unit_price_cents) || 0, line_discount_cents: Number(r.line_discount_cents) || 0 })),
    discount_type: discountType,
    discount_value: discountType === "fixed" ? dollarsToCents(discountValue) : Math.round((parseFloat(discountValue) || 0) * 100),
    tax_rate_bp: Math.round((parseFloat(taxRate) || 0) * 100),
    fee_cents: dollarsToCents(feeDollars),
    payment_terms: paymentTerms || null, valid_until: validUntil || null, notes: notes || null, currency: "USD",
  });

  const saveQuote = async () => {
    setBusy(true);
    try {
      if (quote) await axios.patch(`${API}/org/sales/leads/${lead.lead_id}/quote`, buildPayload(), { withCredentials: true });
      else await axios.post(`${API}/org/sales/leads/${lead.lead_id}/quote`, buildPayload(), { withCredentials: true });
      loadQuote(); onRefresh();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const markReady = async () => {
    setBusy(true);
    try { await axios.post(`${API}/org/sales/leads/${lead.lead_id}/quote/ready`, {}, { withCredentials: true }); loadQuote(); } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const toggleFile = (id) => setSelectedFileIds((ids) => (ids.includes(id) ? ids.filter((i) => i !== id) : [...ids, id]));

  const generateProposal = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/org/sales/leads/${lead.lead_id}/proposal/generate`, { template_id: templateId || null, selected_file_ids: selectedFileIds }, { withCredentials: true });
      loadProposal(); onRefresh();
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const sendProposal = async () => {
    setBusy(true);
    try { await axios.post(`${API}/org/sales/leads/${lead.lead_id}/proposal/send`, {}, { withCredentials: true }); loadProposal(); } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  const copyLink = async () => {
    try {
      const { data } = await axios.get(`${API}/org/sales/leads/${lead.lead_id}/proposal/link`, { withCredentials: true });
      await navigator.clipboard.writeText(data.url);
      onError("");
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`${API}/org/sales/leads/${lead.lead_id}/proposal/pdf`, { withCredentials: true, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${proposal?.display_id || "proposal"}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) { onError(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const quoteVersion = quote ? quote.versions[quote.current_version - 1] : null;
  const proposal = proposalData?.proposal;
  const proposalVersion = proposal ? proposal.versions[proposal.current_version - 1] : null;

  return (
    <div className="space-y-5" data-testid="quote-proposal-tab">
      <div className="flex gap-1 border-b border-slate-200 dark:border-white/10">
        {["quote", "proposal"].map((v) => (
          <button key={v} type="button" onClick={() => setSubView(v)} data-testid={`quote-proposal-subtab-${v}`}
            className={`border-b-2 px-3 py-2 text-sm font-bold capitalize ${subView === v ? "border-[#0052FF] text-[#0052FF]" : "border-transparent text-slate-500 dark:text-slate-400"}`}>
            {v === "quote" ? "Quote" : "Proposal"}
          </button>
        ))}
      </div>

      {subView === "quote" && (
        <div className="space-y-5" data-testid="quote-view">
          {!quote_eligible && !quote ? (
            <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="quote-not-eligible-notice">
              Complete the Assessment (or mark it Not Required) before creating a Quote.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className={labelClass}>{quote ? `${quote.display_id} · v${quote.current_version}` : "New Quote"}</p>
                {quote && <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${COMMERCIAL_STATUS_META[quote.status].cls}`} data-testid="quote-status-badge">{COMMERCIAL_STATUS_META[quote.status].label}</span>}
              </div>

              <div className="space-y-2" data-testid="quote-items-list">
                {rows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 rounded-xl border border-slate-200 p-2.5 dark:border-white/10" data-testid={`quote-item-row-${idx}`}>
                    <input value={row.name} onChange={(e) => updateRow(idx, "name", e.target.value)} placeholder="Item name" data-testid={`quote-item-name-${idx}`} className="col-span-4 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
                    <input type="number" min={1} value={row.quantity} onChange={(e) => updateRow(idx, "quantity", e.target.value)} data-testid={`quote-item-qty-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
                    <input type="number" step="0.01" value={centsToDollars(row.unit_price_cents)} onChange={(e) => updateRow(idx, "unit_price_cents", dollarsToCents(e.target.value))} placeholder="Unit $" data-testid={`quote-item-price-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
                    <input type="number" step="0.01" value={centsToDollars(row.line_discount_cents)} onChange={(e) => updateRow(idx, "line_discount_cents", dollarsToCents(e.target.value))} placeholder="Discount $" data-testid={`quote-item-discount-${idx}`} className="col-span-2 rounded-lg border border-slate-200 px-2 py-1.5 text-xs dark:border-white/15 dark:bg-[#0B1220] dark:text-white" />
                    <button type="button" onClick={() => removeRow(idx)} data-testid={`quote-item-remove-${idx}`} className="col-span-2 rounded-lg border border-slate-200 text-xs font-bold text-red-500 hover:bg-red-50 dark:border-white/15">Remove</button>
                  </div>
                ))}
                <button type="button" onClick={addRow} data-testid="quote-add-item-btn" className={outlineBtn}>+ Add Custom Line</button>
              </div>

              <div className="grid gap-3 sm:grid-cols-4">
                <div>
                  <label className={labelClass}>Discount Type</label>
                  <select value={discountType} onChange={(e) => setDiscountType(e.target.value)} data-testid="quote-discount-type-select" className={inputClass}>
                    <option value="none">None</option><option value="percentage">Percentage</option><option value="fixed">Fixed</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>Discount {discountType === "percentage" ? "(%)" : "($)"}</label>
                  <input type="number" step="0.01" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} data-testid="quote-discount-value-input" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Tax Rate (%)</label>
                  <input type="number" step="0.01" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} data-testid="quote-tax-rate-input" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Fee ($)</label>
                  <input type="number" step="0.01" value={feeDollars} onChange={(e) => setFeeDollars(e.target.value)} data-testid="quote-fee-input" className={inputClass} />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelClass}>Payment Terms</label>
                  <input value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} data-testid="quote-payment-terms-input" className={inputClass} />
                </div>
                <div>
                  <label className={labelClass}>Valid Until</label>
                  <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} data-testid="quote-valid-until-input" className={inputClass} />
                </div>
              </div>
              <div>
                <label className={labelClass}>Internal Notes</label>
                <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="quote-notes-input" className={inputClass} />
              </div>

              {quoteVersion && (
                <div className="rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="quote-totals-card">
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span className="font-bold">{formatCents(quoteVersion.subtotal_cents)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Discount</span><span className="font-bold">-{formatCents(quoteVersion.discount_cents)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Tax</span><span className="font-bold">{formatCents(quoteVersion.tax_cents)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-slate-500">Fee</span><span className="font-bold">{formatCents(quoteVersion.fee_cents)}</span></div>
                  <div className="mt-2 flex justify-between border-t border-slate-200 pt-2 text-base dark:border-white/10"><span className="font-bold">Total</span><span className="font-extrabold text-[#0052FF]" data-testid="quote-total-display">{formatCents(quoteVersion.total_cents)}</span></div>
                </div>
              )}

              <div className="flex gap-2">
                <button type="button" onClick={saveQuote} disabled={busy} data-testid="quote-save-btn" className={btn}>{quote ? "Save Revision" : "Create Quote"}</button>
                {quote?.status === "Draft" && <button type="button" onClick={markReady} disabled={busy} data-testid="quote-mark-ready-btn" className={outlineBtn}>Mark Ready</button>}
              </div>
            </>
          )}
        </div>
      )}

      {subView === "proposal" && (
        <div className="space-y-5" data-testid="proposal-view">
          {!quote ? (
            <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="proposal-no-quote-notice">Create a Quote before generating a Proposal.</p>
          ) : !proposalData ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className={labelClass}>{proposal ? `${proposal.display_id} · v${proposal.current_version}` : "New Proposal"}</p>
                {proposal && <span className={`rounded-full border px-2.5 py-1 text-xs font-bold ${COMMERCIAL_STATUS_META[proposal.status].cls}`} data-testid="proposal-status-badge">{COMMERCIAL_STATUS_META[proposal.status].label}</span>}
              </div>

              {(!proposal || proposal.status === "Draft") && (
                <div className="space-y-3 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="proposal-generate-form">
                  <div>
                    <label className={labelClass}>Template</label>
                    <select value={templateId} onChange={(e) => setTemplateId(e.target.value)} data-testid="proposal-template-select" className={inputClass}>
                      <option value="">Auto (assigned / global default / Nova Standard)</option>
                      <option value="nova_standard_default">Nova Standard</option>
                      {proposalData.available_templates?.map((t) => <option key={t.template_id} value={t.template_id}>{t.name}</option>)}
                    </select>
                  </div>
                  {leadFiles?.length > 0 && (
                    <div>
                      <label className={labelClass}>Include Files</label>
                      <div className="mt-1 max-h-28 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2 dark:border-white/15">
                        {leadFiles.map((f) => (
                          <label key={f.file_id} className="flex items-center gap-2 text-sm">
                            <input type="checkbox" checked={selectedFileIds.includes(f.file_id)} onChange={() => toggleFile(f.file_id)} data-testid={`proposal-file-select-${f.file_id}`} />
                            {f.original_filename}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  <button type="button" onClick={generateProposal} disabled={busy} data-testid="proposal-generate-btn" className={btn}>
                    <Sparkles size={13} /> {proposal ? "Regenerate" : "Generate Proposal"}
                  </button>
                </div>
              )}

              {proposalVersion && (
                <div className="space-y-2" data-testid="proposal-sections-preview">
                  {proposalVersion.sections.map((s, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-3 dark:border-white/10" data-testid={`proposal-section-${s.type}`}>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{s.label}</p>
                      {s.type === "quote_pricing" ? (
                        <p className="mt-1 text-sm font-extrabold text-[#0052FF]">{formatCents(s.content.total_cents)}</p>
                      ) : s.type === "customer_acceptance" ? (
                        <p className="mt-1 text-xs text-slate-400">Customer accepts/declines here.</p>
                      ) : (
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{JSON.stringify(s.content).slice(0, 160)}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {proposal && proposal.status === "Draft" && (
                <div>
                  {!proposalData.email_ready ? (
                    <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 dark:bg-amber-500/10 dark:text-amber-400" data-testid="proposal-email-not-ready-notice">
                      Set your organization's Customer-Facing Email in <a href="/dashboard/settings" className="underline">Settings</a> before sending Proposals.
                    </p>
                  ) : (
                    <button type="button" onClick={sendProposal} disabled={busy} data-testid="proposal-send-btn" className={btn}><Send size={13} /> Send Proposal</button>
                  )}
                </div>
              )}

              {proposal && (
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={copyLink} data-testid="proposal-copy-link-btn" className={outlineBtn}><Copy size={13} /> Copy Proposal Link</button>
                  {proposal.sent_at && <button type="button" onClick={downloadPdf} data-testid="proposal-download-pdf-btn" className={outlineBtn}><Download size={13} /> Download PDF</button>}
                  {proposal.sent_at && <p className="text-xs text-slate-400">Sent {formatDateTime(proposal.sent_at)}</p>}
                  {proposal.first_viewed_at && <p className="text-xs text-slate-400">First viewed {formatDateTime(proposal.first_viewed_at)}</p>}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};
